import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'angular2-toaster';
import { ListViewItem } from 'app/components/common/listViewItem';
import { TagItem } from 'app/components/common/tag/tag.component';
import { PendingChangesAware } from 'app/components/guards/pendingChanges.guard';
import { BasicModalComponent, BasicModalResult } from 'app/components/modal/basicModal.component';
import { UnlinkDeviceModalComponent } from 'app/components/modal/unlinkDeviceModal.component';
import { Globals } from 'app/globals/globals';
import { AuthService } from 'app/services/auth.service';
import { DeviceService } from 'app/services/device.service';
import { DevicesGroupService } from 'app/services/devicesGroup.service';
import { ModalService } from 'app/services/modal.service';
import { PresentationService } from 'app/services/presentation/presentation.service';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { EditScheduleComponent } from 'app/views/devices/editSchedule/editSchedule.component';
import { DeviceModel } from 'app/views/devices/models/device.model';
import { DevicesGroupModel } from 'app/views/devices/models/devicesGroup.model';
import { ScheduleModel } from 'app/views/devices/models/schedule.model';

export enum DeviceConfigurationMode {
    Standalone = 'Standalone',
    Shared = 'Shared'
}

@Component({
    selector: 'psm-edit-device',
    templateUrl: 'editDevice.component.html',
    styleUrls: ['editDevice.component.scss']
})

export class EditDeviceComponent implements OnInit, PendingChangesAware {

    private deviceId: string;
    public groupName: string;
    private logger: Logger;

    public isSaving: boolean = false;
    public tag: TagItem;

    public model: DeviceModel;
    public form: FormGroup;
    public name: FormControl;
    public configurationMode: FormControl;
    public group: FormControl;
    public schedule: FormControl;
    public timezone: FormControl;
    public isTimezoneManual: FormControl;
    public readonly newGroupId: string = `newGroup`;

    public availableGroups: ListViewItem[] = [];
    public configurationModeButtons: ListViewItem[] = [
        {
            key: DeviceConfigurationMode.Standalone,
            label: 'modules.devices.edit.device-schedule-title'
        },
        {
            key: DeviceConfigurationMode.Shared,
            label: 'modules.devices.edit.shared-schedule-title'
        }
    ];

    @ViewChild('scheduleEditor')
    public scheduleEditor: EditScheduleComponent;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toasterService: ToasterService,
        private loggerFactory: LoggerFactory,
        private deviceService: DeviceService,
        private devicesGroupService: DevicesGroupService,
        private modalService: ModalService,
        private globals: Globals,
        private presentationService: PresentationService,
        private authService: AuthService,
        private translateService: TranslateService
    ) {
        this.logger = loggerFactory.create('EditDeviceComponent');
    }

    public async ngOnInit() {
        this.deviceId = this.route.snapshot.params.id;
        let userId = (await this.authService.getCurrentUser()).uid;
        let groups = await this.devicesGroupService.getUserDevicesGroups(userId);

        this.availableGroups.push({
            key: this.newGroupId,
            label: await this.translateService.instant('modules.devices.groups.create-group')
        });

        let groupItems = groups.map(x => {
            return {
                key: x.id,
                label: x.name
            };
        });

        this.availableGroups = this.availableGroups.concat(groupItems);

        if (this.deviceService.deviceExists(this.deviceId)) {
            this.model = await this.deviceService.getDevice(this.deviceId);

            if (!this.model) {
                this.logger.error(`Unable to find device ${this.deviceId}`);
                this.toasterService.pop('error', '', 'modules.devices.edit.error-getting-device');
                this.router.navigate(['devices/list']);
            }

            try {
                if (this.model.metadata.state) {
                    this.tag = await this.deviceService.createDeviceTag(this.model);
                }
            } catch (e) {
                this.logger.warn(`Device '${this.deviceId}' has no metatada`, e);
            }

            if (!this.model.timezone) {
                this.model.isTimezoneManual = false;
            }

            this.ngOnChanges();
        }
    }

    public async ngOnChanges() {
        this.name = new FormControl(this.model.name, [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(this.globals.shortStringLength)
        ]);

        let configurationMode = this.model.groupId ? DeviceConfigurationMode.Shared : DeviceConfigurationMode.Standalone;
        this.configurationMode = new FormControl(configurationMode);

        this.group = new FormControl(this.model.groupId, [
            x => this.validateGroup(x),
            Validators.maxLength(this.globals.shortStringLength)
        ]);

        if (configurationMode === DeviceConfigurationMode.Shared) {
            let deviceGroup = await this.devicesGroupService.getDevicesGroup(this.model.groupId);
            this.groupName = deviceGroup.name;
            // Load group schedule if share configuration
            let group = await this.getGroup();
            this.logger.info('Cloning group schedule.');
            this.schedule = new FormControl(this.setupSchedule(this.clone(group.schedule)), [x => this.validateSchedule(x)]);
        } else {
            // Load device schedule if not shared configuration
            this.logger.info('Cloning device schedule.');
            this.schedule = new FormControl(this.setupSchedule(this.clone(this.model.schedule)), [x => this.validateSchedule(x)]);
        }

        this.isTimezoneManual = new FormControl(this.model.isTimezoneManual);
        this.timezone = new FormControl(this.model.timezone, [x => this.validateTimezone(x)]);

        this.form = new FormGroup({
            name: this.name,
            configurationMode: this.configurationMode,
            group: this.group,
            schedule: this.schedule,
            timezone: this.timezone,
            isTimezoneManual: this.isTimezoneManual
        });
    }

    @HostListener('window:beforeunload')
    public hasPendingChanges() {
        return this.form.dirty;
    }

    private validateGroup(group: AbstractControl): ValidationErrors | null {
        if (this.configurationMode.value === DeviceConfigurationMode.Shared && !group.value) {
            return { required: true };
        }
        return null;
    }

    private validateSchedule(scheduleControl: AbstractControl): ValidationErrors | null {
        if (scheduleControl.value && this.presentationService.scheduleOverlap(scheduleControl.value)) {
            return { overlap: 'overlap' };
        }
        return null;
    }

    private validateTimezone(timezone: AbstractControl): ValidationErrors | null {
        if (this.isTimezoneManual && this.isTimezoneManual.value && !timezone.value) {
            return { required: 'true' };
        }
        return null;
    }

    private setupSchedule(schedule: ScheduleModel): ScheduleModel {
        if (!schedule) {
            schedule = new ScheduleModel();
        }

        if (!schedule.entries) {
            schedule.entries = [];
        }

        if (!schedule.defaultPresentationId) {
            schedule.defaultPresentationId = '';
        }
        return schedule;
    }

    private async getGroup() {
        try {
            let group = await this.devicesGroupService.getDevicesGroup(this.group.value);
            return group;
        } catch (err) {
            this.logger.error(err.message);
            throw err;
        }
    }

    private async openUnlinkDeviceModal() {
        return await this.modalService.open(UnlinkDeviceModalComponent);
    }

    public async unlinkDevice() {
        try {
            let unlinkDeviceModalResponse = await this.openUnlinkDeviceModal();
            if (unlinkDeviceModalResponse) {
                await this.deviceService.unlinkDevice(this.deviceId);
                this.toasterService.pop('success', '', 'modules.devices.edit.unlink-success');
                this.router.navigate(['/devices/list']);
            }
        } catch (err) {
            this.logger.error(err.message);
            this.toasterService.pop('error', '', 'modules.devices.edit.unlink-error');
        }
    }

    public async saveDevice() {
        this.group.updateValueAndValidity();
        if (!this.form.valid) { return; }

        try {
            // Set the view in saving mode
            this.isSaving = true;

            // Open modal if device schedule has been edited and device has a group
            let shouldSaveDevice = await this.agreeOnChangeAndUpdateSchedule();
            if (shouldSaveDevice) {
                this.model.name = this.name.value;
                this.model.timezone = this.timezone.value;
                this.model.isTimezoneManual = this.isTimezoneManual.value;

                // If shared configuration is disabled, groupId should be set to undefined
                if (this.configurationMode.value === DeviceConfigurationMode.Standalone) {
                    this.model.groupId = undefined;
                } else {
                    if (this.group.value === this.newGroupId) {
                        let group = await this.devicesGroupService.createDevicesGroupFromDevice(this.model);
                        this.model.groupId = group.id;
                        this.availableGroups.push({
                            key: group.id,
                            label: group.name
                        });
                    } else {
                        this.model.groupId = this.group.value;
                    }
                }

                await this.deviceService.saveDevice(this.model);

                // Update model data to reflect newly saved configuration.
                this.model = await this.deviceService.getDevice(this.deviceId);

                this.ngOnChanges();
                this.toasterService.pop('success', '', 'modules.devices.edit.saving-success');
            }
        } catch (e) {
            this.logger.error(`An error occured while saving device '${this.model.id}'. ${e.message}`, e);
            this.toasterService.pop('error', '', 'modules.devices.edit.saving-error');
        } finally {
            this.isSaving = false;
        }
    }

    private async agreeOnChangeAndUpdateSchedule() {
        if (this.configurationMode.value === DeviceConfigurationMode.Shared) {
            if (this.schedule.dirty) {
                let devicesGroup = await this.getGroup();
                let nbDeviceInGroup = await this.devicesGroupService.getDevicesOfGroup(devicesGroup.id);
                let response: BasicModalResult;

                // Pop modal if there is more than one device that will be affected by this
                if (nbDeviceInGroup.length > 1) {
                    response = await this.modalService.open<BasicModalComponent, BasicModalResult>(BasicModalComponent,
                        {
                            icon: 'exclamation-triangle',
                            resourcePath: 'modules.devices.edit.group-conflict-modal'
                        });
                } else {
                    // Since the changes is only affecting the current device, 
                    // we automatically consider the user is ready to accept the changes
                    response = BasicModalResult.PrimaryAction;
                }

                switch (response) {
                    case BasicModalResult.Dismiss:
                        return false; // Do not save device

                    case BasicModalResult.PrimaryAction:
                        // Primary modal action is update group settings
                        this.model.groupId = this.group.value;
                        let group = await this.devicesGroupService.getDevicesGroup(this.model.groupId);
                        group.schedule = this.schedule.value;
                        await this.devicesGroupService.saveDevicesGroup(group);
                        this.logger.debug(`Updating group '${group.id}' with device '${this.model.id}' settings.`);
                        break;

                    case BasicModalResult.SecondaryAction:
                        // Secondary modal action is separate device from group
                        this.logger.debug(`Separating device '${this.model.id}' from group '${this.model.groupId}'.`);
                        this.group.setValue(undefined);
                        this.model.groupId = undefined;
                        this.model.schedule = this.schedule.value;
                        break;
                }
            }
        } else {
            // Shared configuration is disabled
            this.model.schedule = this.schedule.value;
        }
        return true; // Proceed to device saving.
    }

    public async onDeviceConfigurationModeChanges(configuration: DeviceConfigurationMode) {
        this.configurationMode.setValue(configuration);
        this.configurationMode.markAsDirty();
        this.configurationMode.updateValueAndValidity();
        this.group.updateValueAndValidity();

        if (configuration === DeviceConfigurationMode.Shared) {
            if (!this.group.value) {
                this.group.setValue(this.newGroupId);
            }

            // Set group schedule
            await this.setScheduleGroup();
        } else {
            // Set model schedule
            await this.setScheduleModel();
        }

    }

    public async onGroupIdChange(event) {
        this.group.setValue(event);
        this.group.markAsDirty();
        this.group.updateValueAndValidity();
        // Set group schedule
        await this.setScheduleGroup();
    }

    public onTimezoneChange(event) {
        this.timezone.setValue(event);
        this.timezone.markAsDirty();
        this.timezone.updateValueAndValidity();
        this.isTimezoneManual.updateValueAndValidity();
    }

    public onIsTimezoneManualChange(event) {
        this.isTimezoneManual.setValue(event);
        this.isTimezoneManual.markAsDirty();

        this.timezone.updateValueAndValidity();
        this.isTimezoneManual.updateValueAndValidity();
    }

    private async setScheduleModel() {
        this.schedule.setValue(this.setupSchedule(this.clone(this.model.schedule)));
        await this.scheduleEditor.ngOnInit();
    }

    private async setScheduleGroup() {
        if (this.group.value) {
            if (this.group.value !== this.newGroupId) {
                let group = await this.getGroup();
                this.schedule.setValue(this.setupSchedule(this.clone(group.schedule)));
            } else {
                // If new group get model schedule
                await this.setScheduleModel();
            }
        }

        await this.scheduleEditor.ngOnInit();
    }

    public cssBtnSelected(value: boolean) {
        if (!value) {
            return 'btn-outline';
        }
        return '';
    }

    private clone(obj: object) {
        if (!obj) {
            this.logger.error('The object you tried to clone is undefined.');
            return;
        }
        return Object.assign({}, JSON.parse(JSON.stringify(obj)));
    }
}



// WEBPACK FOOTER //
// ./src/app/views/devices/editDevice.component.ts